import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { debounce } from "@mui/material/utils";
import { FC } from "react";

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
}

interface Props {
  id: string;
  cN?: string;
  value: any;
  setValue: (value: any) => void;
}

export const AsyncAutocompleteLocation: FC<Props> = ({
  id,
  cN,
  value,
  setValue,
}) => {
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
  const loaded = React.useRef(false);

  if (typeof window !== "undefined" && !loaded.current) {
    // Set callback for Google Maps API.
    // @ts-ignore
    window.googlePlacesApiLoaded = () => {};
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_AND_PLACES_API_KEY_FOR_FRONT_END}&libraries=places&callback=googlePlacesApiLoaded`,
        document.querySelector("head"),
        "google-maps"
      );
    }

    loaded.current = true;
  }

  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        400
      ),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      id={id}
      className={cN}
      classes={{
        root: "bg-white rounded-md",
        inputRoot: "bg-transparent rounded-md",
        // focused: "bg-white",
        input: "text-base bg-transparent bg-white sm:text-sm",
        inputFocused: "focus:ring-0",
        noOptions: "hidden",
        paper: "rounded-none rounded-b-md shadow-sm shadow-black/40",
        listbox: "rounded-none",
      }}
      sx={{
        "& .MuiAutocomplete-inputRoot": {
          // paddingTop: 0,
          // paddingBottom: 0,
        },
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      forcePopupIcon={false} // hides the dropdown icon
      noOptionsText="No locations"
      onChange={(event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue?.description || "");
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          // variant="filled"
          // label="location"
          placeholder="Filter by Country or City..."
          InputProps={{
            ...params.InputProps,
            // disableUnderline: true,
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props}>
            <div className="p-0.5 text-sm">{option.description}</div>

            {/* <Grid container alignItems="center"> */}
            {/* <Grid item sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid> */}
            {/* <Grid item sx={{ wordWrap: "break-word" }}>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <span className="text-xs">
                  {option.structured_formatting.secondary_text}
                </span>
              </Grid> */}
            {/* </Grid> */}
          </li>
        );
      }}
    />
  );
};
